/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useMemo, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import Button from '@/components/Button';
import Markdown from '@/components/Markdown';
import ToastContent from '@/components/ToastContent';

import { PassoExecutado } from '@/models/PassoExecutado';
import {
  ProtocoloExecutado,
  RedirecionamentoProtocolo,
} from '@/models/ProtocoloExecutado';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import {
  CondicaoSolicitacaoType,
  FhirArtefatoType,
} from '@/features/enterprise/models/types';
import {
  getPassoRespostaPayload,
  isValidRespostaPayload,
  listInvalidRespostaPayload,
} from '@/features/execucao/helpers/respostas';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import Feature from '@/features/features/components/Feature';

import { ModoRedirecionamento } from '@/models/OpcaoRedirecionamento';

import { useSolicitacoesContext } from '@/features/solicitacoes/providers/SolicitacoesProvider.index';
import SolicitacaoListSection from '@/features/solicitacoes/components/SolicitacaoListSection';
import { ExecutarPassoPayload, RespostaPayload } from '../../types';

import { usePerguntaContext } from '../../providers/PerguntaProvider';
import ExecucaoAPI from '../../services/ExecucaoAPI';

import ButtonInterromperProtocolo from '../ButtonInterromperProtocolo';
import ButtonOpenFlowchart from '../ButtonOpenFlowchart';
import ButtonSairProtocolo from '../ButtonSairProtocolo';
import ConditionAlert from '../ConditionAlert';
import PassoExecutadoBreakpoint from '../PassoExecutadoBreakpoint';

import ProtocoloExecutadoSumario from '../ProtocoloExecutadoSumario';
import BoxPerguntas, { BoxPerguntasRef } from './BoxPerguntas';
import BoxSugestoesProtocolos from './BoxSugestoesProtocolos';
import ModalRedirecionarProtocolo from './BoxSugestoesProtocolos/ModalRedirecionarProtocolo';
import {
  Container,
  Content,
  DescriptionContainer,
  Footer,
  Header,
  RefazerButton,
} from './styles';
import { TipoPasso } from './types';

export interface PassoExecutadoDetailProps {
  passoExecutado: PassoExecutado;
}

export type PassoExecutadoDetailRef = HTMLDivElement;

const PassoExecutadoDetail = React.forwardRef<
  PassoExecutadoDetailRef,
  PassoExecutadoDetailProps
>(({ passoExecutado }, ref) => {
  const boxPerguntasRef = React.useRef<BoxPerguntasRef>(null);

  const { user } = useAuth();
  const history = useHistory();
  const [{ mode }, { dispatchStartFromExecution }] = useEnterpriseContext();
  const [
    { status, passosExecutados, protocoloExecutado, isProtocoloReadOnly },
    {
      dispatchExecutarPasso,
      dispatchRefazerPasso,
      dispatchPararPasso,
      dispatchProsseguirPasso,
      dispatchSolicitarFinalizacao,
      dispatchAlterarStatus,
      dispatchSetExecucao,
    },
  ] = useExecucaoContext();

  const { hasItemsToRequest, request, cancel } = useSolicitacoesContext();

  const [payload, setPayload] = useState<RespostaPayload>(() =>
    getPassoRespostaPayload(passoExecutado),
  );
  const [selectedRedirects, setSelectedRedirects] = React.useState(() => {
    if (passoExecutado.redirecionamento) {
      return passoExecutado.redirecionamento.opcoes.filter(
        ({ selected }) => selected,
      );
    }

    return [];
  });

  const [{ allowNullItems }] = usePerguntaContext();

  const [inError, setInError] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState(
    (passoExecutado.solicitacoes as unknown) as FhirArtefatoType[],
  );
  const [isProsseguirLoading, setIsProsseguirLoading] = useState(false);
  const [isCancelandoArtefatos, setIsCancelandoArtefatos] = useState(false);
  const [redirectModalOpen, setRedirectModalOpen] = useState(false);

  const integracaoEnterpriseAtiva = useMemo(() => {
    return mode === 'integrated';
  }, [mode]);

  const canExecutar = useMemo(
    () =>
      status === 'active' &&
      isValidRespostaPayload(passoExecutado, payload, allowNullItems),
    [allowNullItems, passoExecutado, payload, status],
  );

  const exibeBreakPoint = useMemo(
    () =>
      passoExecutado.breakpoint &&
      passoExecutado.executado &&
      passoExecutado.id === passosExecutados.slice(-1)[0].id,
    [passoExecutado, passosExecutados],
  );

  const submitText = React.useMemo(() => {
    if (
      hasItemsToRequest({ source: passoExecutado.id }) ||
      selectedRedirects.length >= 1
    ) {
      return passoExecutado.final
        ? 'Prescrever e finalizar'
        : 'Prescrever e prosseguir';
    }

    return passoExecutado.final ? 'Finalizar e fechar' : 'Prosseguir';
  }, [hasItemsToRequest, passoExecutado, selectedRedirects.length]);

  const canRefazer = useMemo(() => {
    return (
      !isProtocoloReadOnly &&
      !exibeBreakPoint &&
      passoExecutado.executado &&
      !protocoloExecutado?.executado &&
      ((passoExecutado.executado_por &&
        passoExecutado.executado_por === user?.username) ||
        !passoExecutado.executado_por)
    );
  }, [
    exibeBreakPoint,
    isProtocoloReadOnly,
    passoExecutado.executado,
    passoExecutado.executado_por,
    protocoloExecutado?.executado,
    user?.username,
  ]);

  const passoType: TipoPasso = React.useMemo(() => {
    if (solicitacoes.some(({ tipo }) => tipo === 'condicao')) {
      return TipoPasso.CONDITION;
    }

    if (passoExecutado.breakpoint) {
      return TipoPasso.BREAKPOINT;
    }

    return TipoPasso.QUESTIONNARIE;
  }, [passoExecutado.breakpoint, solicitacoes]);

  const dispatch = useCallback(
    (dispachPayload: ExecutarPassoPayload) => {
      return {
        [TipoPasso.QUESTIONNARIE]: dispatchExecutarPasso,
        [TipoPasso.CONDITION]: dispatchExecutarPasso,
        [TipoPasso.BREAKPOINT]: dispatchPararPasso,
      }[passoType](dispachPayload);
    },
    [dispatchExecutarPasso, dispatchPararPasso, passoType],
  );

  const handleResposta = useCallback(
    (resposta: RespostaPayload) => {
      setPayload({ ...payload, ...resposta });
    },
    [payload],
  );

  const triggerExecutionFinishEvent = useCallback(
    (execucao?: ProtocoloExecutado) => {
      const eventObject = execucao || protocoloExecutado;
      if (passoExecutado.final && integracaoEnterpriseAtiva && eventObject) {
        // disabled - Goclin - Aguardando Implementação
        // medflowExecutionFinish(eventObject);
      }
    },
    [integracaoEnterpriseAtiva, passoExecutado.final, protocoloExecutado],
  );

  const handleRefazer = useCallback(async () => {
    try {
      setIsCancelandoArtefatos(true);
      await cancel({ source: passoExecutado.id });
    } finally {
      setIsCancelandoArtefatos(false);
    }

    dispatchRefazerPasso({
      passoExecutado,
    });
  }, [passoExecutado, dispatchRefazerPasso, cancel]);

  const directRedirect = useCallback(
    async (target: RedirecionamentoProtocolo) => {
      const { id: targetID, contexto_integracao } = target;
      const { id: targetIntegrationID } = contexto_integracao || {};

      if (mode === 'integrated' && targetIntegrationID) {
        const enterpriseURL = `/enterprise/integracoes/${targetIntegrationID}`;
        history.push(enterpriseURL);
      } else if (targetID) {
        const targetExecution = await ExecucaoAPI.getExecucao(
          targetID.toString(),
        );
        dispatchStartFromExecution(targetExecution);
        dispatchSetExecucao(targetExecution);
      }
    },
    [dispatchSetExecucao, dispatchStartFromExecution, history, mode],
  );

  const handleRedirecionar = useCallback(async () => {
    const { modo } = passoExecutado.redirecionamento || {};

    if (modo === ModoRedirecionamento.DIRETO && !redirectModalOpen) {
      setRedirectModalOpen(true);
      return;
    }

    setIsProsseguirLoading(true);

    const execucao = await ExecucaoAPI.redirecionarFluxo({
      passoExecutado,
      opcoes: selectedRedirects,
    });

    triggerExecutionFinishEvent(execucao);

    if (modo === ModoRedirecionamento.DIRETO) {
      const [destino] = execucao.destino;

      if (destino) directRedirect(destino);
    } else {
      toast(
        () => (
          <ToastContent Left={<FiCheck size={22} />}>
            Protocolos encaminhados com sucesso.
          </ToastContent>
        ),
        {
          position: toast.POSITION.BOTTOM_LEFT,
        },
      );
      dispatchProsseguirPasso({ passoExecutado, protocoloExecutado: execucao });
    }

    setIsProsseguirLoading(false);
  }, [
    directRedirect,
    dispatchProsseguirPasso,
    passoExecutado,
    redirectModalOpen,
    selectedRedirects,
    triggerExecutionFinishEvent,
  ]);

  const handlePrescreverExecutar = useCallback(async () => {
    try {
      if (passoExecutado.final && !integracaoEnterpriseAtiva) {
        dispatchSolicitarFinalizacao({ respostas: payload });

        return;
      }

      setIsProsseguirLoading(true);

      await request({ source: passoExecutado.id });
      await dispatch({ passoExecutado, conduta: { respostas: payload } });
    } catch (error) {
      setInError(true);
      dispatchAlterarStatus({ status: 'on-error' });
    } finally {
      setIsProsseguirLoading(false);
      triggerExecutionFinishEvent();
    }
  }, [
    passoExecutado,
    integracaoEnterpriseAtiva,
    request,
    dispatch,
    payload,
    dispatchSolicitarFinalizacao,
    dispatchAlterarStatus,
    triggerExecutionFinishEvent,
  ]);

  const handleOnProximaEtapa = useCallback(async () => {
    try {
      setIsProsseguirLoading(true);
      if (protocoloExecutado) {
        await dispatchProsseguirPasso({
          passoExecutado: passosExecutados.slice(-1)[0],
          protocoloExecutado,
        });
      }
    } finally {
      dispatchAlterarStatus({ status: 'active' });
      setIsProsseguirLoading(false);
    }
  }, [
    dispatchProsseguirPasso,
    dispatchAlterarStatus,
    passosExecutados,
    protocoloExecutado,
  ]);

  const handleRequireAnswers = React.useCallback(() => {
    if (!inError) {
      const items = listInvalidRespostaPayload(
        passoExecutado,
        payload,
        allowNullItems,
      );

      boxPerguntasRef.current?.require(items);
    }
  }, [allowNullItems, inError, passoExecutado, payload]);

  const ConditionContent = (
    <ConditionAlert
      step={passoExecutado}
      readonly={isProtocoloReadOnly || passoExecutado.executado}
      conditions={
        solicitacoes.filter(
          solic => solic.tipo === 'condicao',
        ) as CondicaoSolicitacaoType[]
      }
      onConditionsConfirmed={conditions => {
        setSolicitacoes(prev =>
          prev.map(req => {
            const conditionConfirmed = conditions.find(
              condition => condition.id === req.id,
            );
            return (conditionConfirmed as FhirArtefatoType) || req;
          }),
        );

        handlePrescreverExecutar();
      }}
      onConditionsRefuted={() => {
        handlePrescreverExecutar();
      }}
    />
  );

  const QuestionnaireContent = (
    <>
      <Header>
        <h2>{passoExecutado.nome}</h2>
      </Header>

      <Content>
        {passoExecutado.descricao && (
          <DescriptionContainer>
            <Markdown text={passoExecutado.descricao} />
          </DescriptionContainer>
        )}

        {passoExecutado.redirecionamento && (
          <>
            {passoExecutado.redirecionamento.modo ===
              ModoRedirecionamento.DIRETO && (
              <ModalRedirecionarProtocolo
                isOpen={redirectModalOpen}
                onRedirecionar={handleRedirecionar}
                onClose={() => setRedirectModalOpen(false)}
              />
            )}
            <BoxSugestoesProtocolos
              disabled={passoExecutado.executado}
              options={passoExecutado.redirecionamento.opcoes}
              value={selectedRedirects}
              onChange={setSelectedRedirects}
              multiple={passoExecutado.redirecionamento.multipla_escolha}
            />
          </>
        )}

        {passoExecutado.perguntas.length > 0 && (
          <BoxPerguntas
            ref={boxPerguntasRef}
            passoExecutado={passoExecutado}
            handleResposta={handleResposta}
          />
        )}

        <SolicitacaoListSection passo={passoExecutado} />

        {passoExecutado.sumarizar_execucao && (
          <ProtocoloExecutadoSumario showBackToProtocol={false} />
        )}
      </Content>

      {!passoExecutado.executado && (
        <Footer>
          <ButtonOpenFlowchart style={{ marginRight: 'auto' }} />

          <Feature
            name="INTERROMPER_PROTOCOLO"
            fallback={<ButtonSairProtocolo />}
          >
            <ButtonInterromperProtocolo />
          </Feature>
          <Button
            theme="primary"
            aria-disabled={!canExecutar}
            loading={isProsseguirLoading}
            onClick={() => {
              if (selectedRedirects.length >= 1) {
                handleRedirecionar();
              } else {
                handlePrescreverExecutar();
              }
            }}
            onAriaDisabledClick={handleRequireAnswers}
          >
            {submitText}
          </Button>
        </Footer>
      )}
    </>
  );

  const BreakPointContent = exibeBreakPoint ? (
    <PassoExecutadoBreakpoint handleOnProximaEtapa={handleOnProximaEtapa} />
  ) : (
    QuestionnaireContent
  );

  const PassoContentMap: { [T in TipoPasso]: JSX.Element } = {
    [TipoPasso.QUESTIONNARIE]: QuestionnaireContent,
    [TipoPasso.CONDITION]: ConditionContent,
    [TipoPasso.BREAKPOINT]: BreakPointContent,
  };

  return (
    <Container ref={ref} $showRefazerButton={canRefazer}>
      {PassoContentMap[passoType]}
      {passoExecutado.executado && canRefazer && (
        <Footer $noTopBorder>
          <RefazerButton
            theme="secondary"
            onClick={handleRefazer}
            loading={isCancelandoArtefatos}
            passoType={passoType}
          >
            Refazer este passo
          </RefazerButton>
        </Footer>
      )}
    </Container>
  );
});

export default PassoExecutadoDetail;
